/**
 * Dialog showing the approval history (approve/reject entries) for a product.
 * Data comes from GET /api/approval-logs/{productId}.
 * Also lets admins/editors record a new approve/reject decision inline.
 */
import { useEffect, useState } from 'react';
import { approvalsAPI } from '@/lib/api';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogFooter } from '@/components/ui/dialog';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { Textarea } from '@/components/ui/textarea';
import { Label } from '@/components/ui/label';
import { History, CheckCircle2, XCircle, Loader2 } from 'lucide-react';
import { toast } from 'sonner';
import { useAuth } from '@/context/AuthContext';

const fmt = (v) => (v ? new Date(v).toLocaleString() : '—');

export default function ApprovalHistoryDialog({ productId, productName, open, onOpenChange }) {
  const { user } = useAuth();
  const [logs, setLogs] = useState([]);
  const [loading, setLoading] = useState(false);
  const [comments, setComments] = useState('');
  const [submitting, setSubmitting] = useState(false);

  const canApprove = user && (user.role === 'admin' || user.role === 'editor');

  const load = async () => {
    if (!productId) return;
    setLoading(true);
    try {
      const res = await approvalsAPI.list(productId);
      setLogs(res.data.logs || []);
    } catch (err) {
      toast.error(err?.response?.data?.detail || 'Failed to load approval history');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => { if (open) load(); /* eslint-disable-next-line */ }, [open, productId]);

  const submit = async (action) => {
    setSubmitting(true);
    try {
      await approvalsAPI.create(productId, action, comments);
      toast.success(action === 'approve' ? 'PCF approved' : 'PCF rejected');
      setComments('');
      await load();
    } catch (err) {
      toast.error(err?.response?.data?.detail || `Failed to ${action}`);
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-2xl" data-testid="approval-history-dialog">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2">
            <History className="w-5 h-5 text-primary" /> Approval History
          </DialogTitle>
          <DialogDescription>
            Audit trail of human-review decisions on <span className="font-semibold">{productName || productId}</span>.
          </DialogDescription>
        </DialogHeader>

        <div className="max-h-80 overflow-y-auto border border-border rounded-md" data-testid="approval-history-list">
          {loading ? (
            <div className="py-10 text-center text-muted-foreground"><Loader2 className="w-5 h-5 animate-spin inline" /></div>
          ) : logs.length === 0 ? (
            <div className="py-10 text-center text-sm text-muted-foreground" data-testid="no-approval-logs">
              No approval decisions recorded yet.
            </div>
          ) : (
            <table className="w-full text-sm">
              <thead className="bg-muted/50 text-left text-xs uppercase tracking-wider text-muted-foreground">
                <tr>
                  <th className="px-3 py-2 font-medium">When</th>
                  <th className="px-3 py-2 font-medium">Reviewer</th>
                  <th className="px-3 py-2 font-medium">Action</th>
                  <th className="px-3 py-2 font-medium">Comments</th>
                </tr>
              </thead>
              <tbody className="divide-y divide-border">
                {logs.map((l, i) => (
                  <tr key={l.id || i} data-testid={`approval-log-row-${i}`}>
                    <td className="px-3 py-2 text-xs text-muted-foreground whitespace-nowrap">{fmt(l.timestamp)}</td>
                    <td className="px-3 py-2 text-xs">{l.reviewer_email || l.reviewer_name || '—'}</td>
                    <td className="px-3 py-2">
                      {l.action === 'approve' ? (
                        <Badge className="bg-green-600/10 text-green-700 dark:text-green-400 border-green-600/20">
                          <CheckCircle2 className="w-3 h-3 mr-1" /> Approved
                        </Badge>
                      ) : (
                        <Badge variant="secondary" className="bg-destructive/10 text-destructive border-destructive/20">
                          <XCircle className="w-3 h-3 mr-1" /> Rejected
                        </Badge>
                      )}
                    </td>
                    <td className="px-3 py-2 text-xs text-muted-foreground">{l.comments || '—'}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          )}
        </div>

        {canApprove && (
          <div className="space-y-2 pt-3 border-t border-border">
            <Label htmlFor="approval-comments" className="text-sm">Add decision (admin / editor only)</Label>
            <Textarea
              id="approval-comments"
              value={comments}
              onChange={(e) => setComments(e.target.value)}
              placeholder="Short reason for the decision (optional but recommended for audit trail)"
              rows={2}
              data-testid="approval-comments-input"
            />
          </div>
        )}

        <DialogFooter>
          <Button variant="outline" onClick={() => onOpenChange(false)} data-testid="close-approval-dialog-btn">Close</Button>
          {canApprove && (
            <>
              <Button
                variant="outline"
                className="text-destructive hover:text-destructive border-destructive/30"
                disabled={submitting}
                onClick={() => submit('reject')}
                data-testid="reject-btn"
              >
                {submitting ? <Loader2 className="w-4 h-4 mr-2 animate-spin" /> : <XCircle className="w-4 h-4 mr-2" />}
                Reject
              </Button>
              <Button
                className="bg-primary hover:bg-primary/90"
                disabled={submitting}
                onClick={() => submit('approve')}
                data-testid="approve-btn"
              >
                {submitting ? <Loader2 className="w-4 h-4 mr-2 animate-spin" /> : <CheckCircle2 className="w-4 h-4 mr-2" />}
                Approve
              </Button>
            </>
          )}
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
